import { RefObject, forwardRef, createRef, Component } from 'react';
import { BubbleChartQuadrantSettings } from './types';
import { SecondaryButton, PrimaryButton, ButtonGroup } from '@ardoq/button';
import { ModalSize, ModalTemplate } from '@ardoq/modal';
import { TextInput } from '@ardoq/forms';
import { Paragraph } from '@ardoq/typography';
import { FlexBox, Stack } from '@ardoq/layout';

export type QuadrantCustomizerLabels = {
  headerText: string;
  cancelButtonText: string;
  applyButtonText: string;
  quadrantLabelsTitle: string;
  xMinimumLabel: string;
  xMaximumLabel: string;
  yMinimumLabel: string;
  yMaximumLabel: string;
  autoPlaceholder: string;
};

const DEFAULT_QUADRANT_CUSTOMIZER_LABELS: QuadrantCustomizerLabels = {
  headerText: 'Custom Quadrants',
  cancelButtonText: 'Cancel',
  applyButtonText: 'Apply',
  quadrantLabelsTitle: 'Quadrant Labels',
  xMinimumLabel: 'X Minimum:',
  xMaximumLabel: 'X Maximum:',
  yMinimumLabel: 'Y Minimum:',
  yMaximumLabel: 'Y Maximum:',
  autoPlaceholder: 'Auto',
};

interface QuadrantCustomizerProperties {
  initialValue: BubbleChartQuadrantSettings;
  close: (result: BubbleChartQuadrantSettings) => void;
  labels?: Partial<QuadrantCustomizerLabels>;
}

const getInputValue = (input: RefObject<HTMLInputElement | null>) => {
  if (!input.current) {
    return NaN;
  }
  const valueAsNumber = input.current.valueAsNumber;
  return valueAsNumber === null ? NaN : valueAsNumber;
};

const LabeledInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { label: string }
>((props, ref) => (
  <TextInput
    label={props.label}
    ref={ref}
    type={props.type}
    defaultValue={props.defaultValue}
    placeholder={props.placeholder}
  />
));
class QuadrantCustomizer extends Component<QuadrantCustomizerProperties> {
  private xMinimumInput = createRef<HTMLInputElement>();
  private xMaximumInput = createRef<HTMLInputElement>();
  private yMinimumInput = createRef<HTMLInputElement>();
  private yMaximumInput = createRef<HTMLInputElement>();
  private quadrant1LabelInput = createRef<HTMLInputElement>();
  private quadrant2LabelInput = createRef<HTMLInputElement>();
  private quadrant3LabelInput = createRef<HTMLInputElement>();
  private quadrant4LabelInput = createRef<HTMLInputElement>();

  private cancel = () => this.props.close(this.props.initialValue);
  private getQuadrantSettings = (): BubbleChartQuadrantSettings => ({
    xMinimum: getInputValue(this.xMinimumInput),
    xMaximum: getInputValue(this.xMaximumInput),
    yMinimum: getInputValue(this.yMinimumInput),
    yMaximum: getInputValue(this.yMaximumInput),
    labels: [
      this.quadrant1LabelInput.current!.value,
      this.quadrant2LabelInput.current!.value,
      this.quadrant3LabelInput.current!.value,
      this.quadrant4LabelInput.current!.value,
    ],
  });
  render() {
    const labels: QuadrantCustomizerLabels = {
      ...DEFAULT_QUADRANT_CUSTOMIZER_LABELS,
      ...this.props.labels,
    };
    return (
      <ModalTemplate
        modalSize={ModalSize.S}
        headerText={labels.headerText}
        onCloseButtonClick={this.cancel}
        footerContent={
          <ButtonGroup>
            <SecondaryButton onClick={this.cancel}>
              {labels.cancelButtonText}
            </SecondaryButton>
            <PrimaryButton
              onClick={() => this.props.close(this.getQuadrantSettings())}
            >
              {labels.applyButtonText}
            </PrimaryButton>
          </ButtonGroup>
        }
      >
        <FlexBox padding="medium" justify="center">
          <Stack gap="small">
            <FlexBox justify="center" marginBottom="xsmall">
              <Paragraph variant="text1Bold">
                {labels.quadrantLabelsTitle}
              </Paragraph>
            </FlexBox>
            <FlexBox gap="small">
              <TextInput
                ref={this.quadrant1LabelInput}
                defaultValue={this.props.initialValue.labels[0]}
              />
              <TextInput
                ref={this.quadrant2LabelInput}
                defaultValue={this.props.initialValue.labels[1]}
              />
            </FlexBox>
            <FlexBox gap="small">
              <TextInput
                ref={this.quadrant3LabelInput}
                defaultValue={this.props.initialValue.labels[2]}
              />
              <TextInput
                ref={this.quadrant4LabelInput}
                defaultValue={this.props.initialValue.labels[3]}
              />
            </FlexBox>

            <FlexBox paddingTop="small" gap="small">
              <LabeledInput
                label={labels.xMinimumLabel}
                type="number"
                ref={this.xMinimumInput}
                defaultValue={`${this.props.initialValue.xMinimum}`}
                placeholder={labels.autoPlaceholder}
              />
              <LabeledInput
                label={labels.xMaximumLabel}
                type="number"
                ref={this.xMaximumInput}
                defaultValue={`${this.props.initialValue.xMaximum}`}
                placeholder={labels.autoPlaceholder}
              />
            </FlexBox>
            <FlexBox gap="small">
              <LabeledInput
                label={labels.yMinimumLabel}
                type="number"
                ref={this.yMinimumInput}
                defaultValue={`${this.props.initialValue.yMinimum}`}
                placeholder={labels.autoPlaceholder}
              />
              <LabeledInput
                label={labels.yMaximumLabel}
                type="number"
                ref={this.yMaximumInput}
                defaultValue={`${this.props.initialValue.yMaximum}`}
                placeholder={labels.autoPlaceholder}
              />
            </FlexBox>
          </Stack>
        </FlexBox>
      </ModalTemplate>
    );
  }
}
export { QuadrantCustomizer };
